// Custom Reports — API, biçimlendirme, tarih ve widget varsayılanları.

const API = 'https://api.glowytics.com/api/custom-reports';

async function call(brandId, path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

export const api = {
  sources: (brandId) => call(brandId, '/sources'),
  list: (brandId) => call(brandId, '/'),
  get: (brandId, id) => call(brandId, `/${id}`),
  create: (brandId, body) => call(brandId, '/', { method: 'POST', body }),
  update: (brandId, id, body) => call(brandId, `/${id}`, { method: 'PUT', body }),
  duplicate: (brandId, id) => call(brandId, `/${id}/duplicate`, { method: 'POST' }),
  remove: (brandId, id) => call(brandId, `/${id}`, { method: 'DELETE' }),
  query: (brandId, body, signal) => call(brandId, '/query', { method: 'POST', body, signal }),
  ai: (brandId, body) => call(brandId, '/ai', { method: 'POST', body }),
};

// Aynı sorgu yeniden boyutlandırma / yeniden çizimde tekrar gitmesin.
const queryCache = new Map();
const QUERY_TTL_MS = 5 * 60 * 1000;

export async function cachedQuery(brandId, body, signal) {
  const key = `${brandId}:${JSON.stringify(body)}`;
  const hit = queryCache.get(key);
  if (hit && Date.now() - hit.at < QUERY_TTL_MS) return hit.value;
  const value = await api.query(brandId, body, signal);
  queryCache.set(key, { at: Date.now(), value });
  return value;
}

export function clearQueryCache() {
  queryCache.clear();
}

// ─── Biçimlendirme ───────────────────────────────────────────────────────────
const nf0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nfCompact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

const CURRENCY_SYMBOL = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };

export function fmtValue(v, fmt, currency = 'TRY', compact = false) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const sym = CURRENCY_SYMBOL[currency] || `${currency} `;
  switch (fmt) {
    case 'currency':
      return `${sym}${compact && Math.abs(v) >= 10000 ? nfCompact.format(v) : (Math.abs(v) < 100 ? nf2 : nf0).format(v)}`;
    case 'percent':
      return `${nf2.format(v * 100)}%`;
    case 'ratio':
      return `${nf2.format(v)}x`;
    case 'decimal':
      return nf2.format(v);
    default:
      return compact && Math.abs(v) >= 10000 ? nfCompact.format(v) : nf0.format(v);
  }
}

const AYLAR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const GUNLER = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function fmtDim(value, dimKey) {
  if (value === null || value === undefined) return '(empty)';
  const s = String(value);
  if (dimKey === 'date_day' || dimKey === 'date_week') {
    const [, m, d] = s.split('-');
    return `${AYLAR[Number(m) - 1]} ${Number(d)}`;
  }
  if (dimKey === 'date_month') {
    const [y, m] = s.split('-');
    return `${AYLAR[Number(m) - 1]} ${y}`;
  }
  if (dimKey === 'weekday') return GUNLER[Number(s)] || s;
  // Google Ads enum'ları: "AdvertisingChannelType.PERFORMANCE_MAX" → "Performance Max"
  const en = s.match(/^[A-Za-z]+\.([A-Z0-9_]+)$/);
  if (en) return en[1].toLowerCase().split('_').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  return s;
}

// ─── Tarih aralıkları ────────────────────────────────────────────────────────
export const DATE_PRESETS = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last14', label: 'Last 14 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 90 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'thisYear', label: 'This year' },
  { key: 'custom', label: 'Custom range' },
];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// "Son N gün" dünde biter: bugünün verisi platformlarda henüz eksik.
export function resolveRange(range) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const back = (n) => { const d = new Date(yesterday); d.setDate(d.getDate() - (n - 1)); return d; };
  switch (range?.preset) {
    case 'last7': return { start: iso(back(7)), end: iso(yesterday) };
    case 'last14': return { start: iso(back(14)), end: iso(yesterday) };
    case 'last90': return { start: iso(back(90)), end: iso(yesterday) };
    case 'thisMonth': return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(today) };
    case 'lastMonth': return {
      start: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      end: iso(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
    case 'thisYear': return { start: iso(new Date(today.getFullYear(), 0, 1)), end: iso(today) };
    case 'custom':
      if (range.start && range.end && range.start <= range.end) return { start: range.start, end: range.end };
      return { start: iso(back(30)), end: iso(yesterday) };
    default: return { start: iso(back(30)), end: iso(yesterday) };
  }
}

export function rangeLabel(range) {
  if (range?.preset === 'custom') {
    const { start, end } = resolveRange(range);
    return `${fmtDim(start, 'date_day')} – ${fmtDim(end, 'date_day')}`;
  }
  return DATE_PRESETS.find((p) => p.key === range?.preset)?.label || 'Last 30 days';
}

// ─── Renkler ─────────────────────────────────────────────────────────────────
// Doğrulanmış 8'li kategorik palet; sabit sırada atanır, döngüye girmez.
// 8'den fazla seri "Diğer"e katlanır (gri).
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
export const OTHER_COLOR = '#9ca3af';
export const MAX_SERIES = 7;

// Platform renkleri sabit: filtre seri sayısını değiştirse de platform rengi değişmez.
const PLATFORM_COLORS = {
  'Google Ads': SERIES_COLORS[0],
  'Meta Ads': SERIES_COLORS[1],
  'TikTok Ads': SERIES_COLORS[2],
  'Criteo': SERIES_COLORS[3],
  'Pinterest Ads': SERIES_COLORS[4],
  'RTB House': SERIES_COLORS[6],
};

export function colorFor(name, index) {
  if (name === 'Other') return OTHER_COLOR;
  return PLATFORM_COLORS[name] || SERIES_COLORS[index % SERIES_COLORS.length];
}

// ─── Widget türleri ──────────────────────────────────────────────────────────
export const WIDGET_TYPES = [
  { key: 'kpi', label: 'KPI card', size: { w: 3, h: 5 } },
  { key: 'line', label: 'Line chart', size: { w: 6, h: 10 } },
  { key: 'area', label: 'Area chart', size: { w: 6, h: 10 } },
  { key: 'bar', label: 'Column chart', size: { w: 6, h: 10 } },
  { key: 'stacked', label: 'Stacked columns', size: { w: 6, h: 10 } },
  { key: 'hbar', label: 'Bar chart', size: { w: 6, h: 10 } },
  { key: 'pie', label: 'Donut chart', size: { w: 4, h: 10 } },
  { key: 'table', label: 'Table', size: { w: 6, h: 11 } },
  { key: 'pivot', label: 'Pivot table', size: { w: 12, h: 12 } },
  { key: 'text', label: 'Text / heading', size: { w: 12, h: 3 } },
];

export const DATE_DIM_KEYS = ['date_day', 'date_week', 'date_month', 'weekday'];
export const isDateDim = (k) => DATE_DIM_KEYS.includes(k);

let seq = 0;
export const newId = (prefix = 'w') => `${prefix}${Date.now().toString(36)}${(seq++).toString(36)}`;

// ─── Logolar ─────────────────────────────────────────────────────────────────
export const LOGOS = {
  google: '/google_ads-512.webp',
  meta: '/6033716.png',
  tiktok: '/tiktokv4.svg',
  criteo: '/criteo-icon.svg',
  rtb: '/RTBhouseiconPNG.png',
  pinterest: '/pinterest-logo.svg',
  ga4: '/GA4logoicon.svg',
  gsc: '/search-console-icon-2025-1v33.svg',
  shopify: '/shopify_glyph.svg',
  ikas: '/ikaslogoicon.png',
  woo: '/woocommerceicon.png',
};

const SOURCE_LOGO = {
  google_ads: 'google', meta_ads: 'meta', meta_breakdown: 'meta', tiktok_ads: 'tiktok', criteo: 'criteo',
  rtb_house: 'rtb', pinterest: 'pinterest', ga4_traffic: 'ga4', ga4_device: 'ga4', ga4_daily: 'ga4',
  search_console: 'gsc', search_console_page: 'gsc', shopify: 'shopify', ikas: 'ikas', woocommerce: 'woo',
};
export const sourceLogo = (sourceKey) => SOURCE_LOGO[sourceKey] || null;

const PLATFORM_LOGO = {
  'Google Ads': 'google', 'Meta Ads': 'meta', 'TikTok Ads': 'tiktok', 'Criteo': 'criteo',
  'RTB House': 'rtb', 'Pinterest Ads': 'pinterest',
};
const LOGO_DIMS = new Set(['platform', 'source', 'source_medium', 'publisher_platform']);

// Boyut değerinden logo: Platform adları birebir, kaynak/yayın platformu desenle.
export function logoForValue(dimKey, value) {
  if (!LOGO_DIMS.has(dimKey) || !value) return null;
  if (PLATFORM_LOGO[value]) return PLATFORM_LOGO[value];
  if (dimKey === 'platform') return null;
  const v = String(value).toLowerCase();
  if (/^(facebook|instagram|ig|fb|meta|messenger|audience_network|an)\b/.test(v)) return 'meta';
  if (/^google\b/.test(v)) return 'google';
  if (/^tiktok/.test(v)) return 'tiktok';
  if (/^pinterest/.test(v)) return 'pinterest';
  if (/^criteo/.test(v)) return 'criteo';
  if (/^rtb/.test(v)) return 'rtb';
  return null;
}

// ─── Widget modeli ───────────────────────────────────────────────────────────
// parts:   [{ id, source, filters, alias }]  — birleştirilen veri kaynakları
// metrics: ["<partId>:<metrik>" | "calc:<id>"] — gösterilen değerler, sırayla
// calcs:   [{ id, label, a, op, b, fmt }]      — kaynaklar arası formüller
export const OPS = [
  { key: '/', label: '÷' }, { key: '*', label: '×' }, { key: '-', label: '−' }, { key: '+', label: '+' },
];
export const CALC_FORMATS = [
  { key: 'currency', label: 'Currency' }, { key: 'number', label: 'Number' }, { key: 'percent', label: 'Percent' },
  { key: 'ratio', label: 'Ratio (x)' }, { key: 'decimal', label: 'Decimal' },
];
export const FMT_BADGE = { currency: '₺', percent: '%', ratio: 'x', decimal: '0.0', number: '#' };

export const parseRef = (ref) => {
  const s = String(ref || '');
  if (s.startsWith('calc:')) return { calc: s.slice(5) };
  const i = s.indexOf(':');
  return { partId: s.slice(0, i), metric: s.slice(i + 1) };
};

// Eski (tek kaynaklı) widget'ları yeni modele taşır.
export function normalizeWidget(w) {
  if (!w || w.type === 'text' || Array.isArray(w.parts)) return w;
  const pid = 'p1';
  const old = (w.metrics || []).filter(Boolean);
  const { source, filters, ...rest } = w;
  return {
    ...rest,
    parts: [{ id: pid, source, filters: filters || [] }],
    metrics: old.map((m) => `${pid}:${m}`),
    calcs: [],
    sort: w.sort?.key && old.includes(w.sort.key) ? { ...w.sort, key: `${pid}:${w.sort.key}` } : w.sort,
  };
}

// Tüm parçalarda ortak olan boyutlar (birleştirme ancak bunlarda yapılabilir).
export function commonDims(w, sourcesByKey) {
  const lists = (w.parts || []).map((p) => sourcesByKey[p.source]?.dimensions || []);
  if (!lists.length) return [];
  return lists[0].filter((d) => lists.every((l) => l.some((x) => x.key === d.key)));
}

// Bir widget'taki değer referansları için etiket/biçim çözücü.
export function makeCtx(w, sourcesByKey) {
  const multi = (w.parts || []).length > 1;
  const dims = commonDims(w, sourcesByKey);
  const metric = (ref, depth = 0) => {
    const r = parseRef(ref);
    if (r.calc) {
      const c = (w.calcs || []).find((x) => x.id === r.calc);
      if (!c) return { ref, label: 'Deleted formula', fullLabel: 'Deleted formula', fmt: 'number', derived: true, missing: true };
      const auto = depth > 2 ? 'Formula' : `${metric(c.a, depth + 1).fullLabel} ${OPS.find((o) => o.key === c.op)?.label || c.op} ${metric(c.b, depth + 1).fullLabel}`;
      const label = c.label || auto;
      // "X başına maliyet" gibi para ÷ … formüllerinde düşük olan iyidir.
      const inverse = c.inverse ?? (c.op === '/' && (c.fmt || 'decimal') === 'currency');
      return { ref, label, fullLabel: label, fmt: c.fmt || 'decimal', derived: true, isCalc: true, inverse };
    }
    const part = (w.parts || []).find((p) => p.id === r.partId);
    const src = part && sourcesByKey[part.source];
    const m = src?.metrics?.find((x) => x.key === r.metric);
    if (!m) return { ref, label: r.metric || ref, fullLabel: r.metric || ref, fmt: 'number', missing: true };
    const partLabel = part.alias || src.label;
    return {
      ref, label: m.label, fmt: m.fmt, inverse: m.inverse, derived: m.derived,
      partLabel, logo: sourceLogo(part.source), sourceKey: part.source,
      fullLabel: multi ? `${partLabel} · ${m.label}` : m.label,
    };
  };
  const dim = (key) => dims.find((d) => d.key === key) || { key, label: key };
  return { multi, dims, metric, dim };
}

function firstNonDate(dims) {
  return dims.find((d) => !d.isDate)?.key || 'date_month';
}

export function newPart(sourceKey) {
  return { id: newId('p'), source: sourceKey, filters: [] };
}

function pickMetricRef(part, source) {
  const keys = source?.metrics?.map((m) => m.key) || [];
  const k = ['cost', 'revenue', 'sessions', 'clicks', 'orders'].find((x) => keys.includes(x)) || keys[0];
  return k ? `${part.id}:${k}` : null;
}

// Tür değişince ya da yeni widget'ta boyut/limit gibi türe özgü alanlar.
export function typeDefaults(type, dims, metrics) {
  const one = metrics.slice(0, 1);
  switch (type) {
    case 'kpi': return { metrics: one, compare: 'previous' };
    case 'line':
    case 'area': return { metrics: metrics.slice(0, 4), dimension: 'date_day', breakdown: '' };
    case 'bar':
    case 'hbar': return { metrics: metrics.slice(0, 4), dimension: firstNonDate(dims), breakdown: '', limit: 10 };
    case 'stacked': return { metrics: one, dimension: 'date_week', breakdown: firstNonDate(dims), limit: 20 };
    case 'pie': return { metrics: one, dimension: firstNonDate(dims), limit: 6 };
    case 'table': return { metrics, dimension: firstNonDate(dims), dimension2: '', limit: 25 };
    case 'pivot': return { metrics: one, pivotRow: firstNonDate(dims), pivotCol: 'date_month' };
    default: return {};
  }
}

export function newWidget(type, source, sourcesByKey) {
  if (type === 'text') return { type, text: 'Heading', textSize: 'xl', textBold: true, textAlign: 'left' };
  const part = newPart(source?.key);
  const first = pickMetricRef(part, source);
  let metrics = first ? [first] : [];
  if (type === 'table') {
    const extra = ['clicks', 'conversions', 'roas', 'users', 'transactions']
      .filter((k) => source?.metrics?.some((m) => m.key === k) && `${part.id}:${k}` !== first)
      .map((k) => `${part.id}:${k}`);
    metrics = [...metrics, ...extra].slice(0, 4);
  }
  const w = { type, parts: [part], calcs: [], dateMode: 'report' };
  return { ...w, ...typeDefaults(type, commonDims(w, sourcesByKey), metrics) };
}

// ─── AI ile kurma ────────────────────────────────────────────────────────────
// Model çıktısı (parça indeksli, düz alanlı) → widget modeli. Katalogda olmayan
// kaynak/metrik/boyut atılır, eksikler tür varsayılanlarıyla doldurulur; atılan
// her şey `notes`'a yazılır ki kullanıcı sessiz değişikliği cevapta görsün.
const CHART_TYPES = ['line', 'area', 'bar', 'hbar', 'stacked'];
const FILTER_OPS = ['contains', 'not_contains', 'equals', 'not_equals', 'in', 'not_in'];
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

// AI tarih alanları → { preset } | { preset: 'custom', start, end }; geçersizse null.
export function aiDateRange(preset, start, end) {
  if (!preset || preset === 'keep' || preset === 'report') return null;
  if (preset !== 'custom') return DATE_PRESETS.some((p) => p.key === preset) ? { preset } : null;
  return ISO_DAY.test(start || '') && ISO_DAY.test(end || '') && start <= end ? { preset: 'custom', start, end } : null;
}

export function aiToWidget(a, sourcesByKey, notes = []) {
  const t = WIDGET_TYPES.find((x) => x.key === a?.type);
  if (!t) return null;
  const size = { w: clamp(a.width || t.size.w, 2, 12), h: a.height > 0 ? clamp(a.height, 2, 30) : t.size.h };
  if (a.type === 'text') {
    return { type: 'text', title: '', text: a.text || 'Heading', textSize: a.textSize && a.textSize !== 'base' ? a.textSize : 'xl', textBold: true, textAlign: 'left', size };
  }

  const partByIndex = {};
  const parts = [];
  const label = a.title || t.label;
  (a.parts || []).forEach((p, i) => {
    const src = sourcesByKey[p.source];
    if (!src || !src.connected || parts.length >= 5) {
      notes.push(`${label}: "${p.source}" isn't available for this brand, so it was left out.`);
      return;
    }
    const dimKeys = new Set(src.dimensions.filter((d) => !d.isDate).map((d) => d.key));
    const filters = (p.filters || []).filter((f) => f.value);
    const okFilters = filters.filter((f) => dimKeys.has(f.dimension) && FILTER_OPS.includes(f.op));
    if (okFilters.length < filters.length) notes.push(`${label}: ${filters.length - okFilters.length} filter(s) didn't fit ${src.label} and were dropped.`);
    const part = {
      id: newId('p'), source: p.source, alias: p.alias || '',
      filters: okFilters.map((f) => ({ dimension: f.dimension, op: f.op, value: f.value })),
    };
    partByIndex[i] = part;
    parts.push(part);
  });
  if (!parts.length) return null;

  const ref = (r) => {
    const part = partByIndex[r?.part];
    const ok = part && sourcesByKey[part.source].metrics.some((m) => m.key === r.metric);
    return ok ? `${part.id}:${r.metric}` : null;
  };
  const calcByIndex = {};
  const calcs = [];
  (a.calcs || []).forEach((c, i) => {
    const ca = ref(c.a);
    const cb = ref(c.b);
    if (!ca || !cb) return;
    const calc = { id: newId('c'), label: c.label || '', a: ca, op: c.op, b: cb, fmt: c.fmt || 'decimal' };
    calcByIndex[i] = calc;
    calcs.push(calc);
  });
  const asked = (a.metrics || []).length;
  let metrics = [...new Set((a.metrics || []).map((m) => (m.calc >= 0 ? (calcByIndex[m.calc] ? `calc:${calcByIndex[m.calc].id}` : null) : ref(m))).filter(Boolean))];
  if (metrics.length < asked) notes.push(`${label}: some requested metrics aren't available in the chosen source and were left out.`);
  if (!metrics.length) {
    const first = pickMetricRef(parts[0], sourcesByKey[parts[0].source]);
    if (first) metrics = [first];
  }

  const base = { type: a.type, title: a.title || '', parts, calcs, metrics, dateMode: 'report' };
  const ownRange = aiDateRange(a.datePreset, a.dateStart, a.dateEnd);
  if (ownRange) { base.dateMode = 'custom'; base.dateRange = ownRange; }
  const dims = commonDims(base, sourcesByKey);
  const def = typeDefaults(a.type, dims, metrics);
  const dim = (k, only) => {
    const ok = k && dims.some((d) => d.key === k && (only !== 'date' || d.isDate) && (only !== 'nodate' || !d.isDate));
    if (k && !ok) notes.push(`${label}: "${k}" can't be used here, the default was used instead.`);
    return ok ? k : '';
  };
  const ctx = makeCtx(base, sourcesByKey);

  let w = { ...base, ...def };
  switch (a.type) {
    case 'kpi':
      w.compare = a.compare || 'previous';
      break;
    case 'line': case 'area': case 'bar': case 'hbar': case 'stacked': {
      w.dimension = dim(a.dimension, ['line', 'area'].includes(a.type) ? 'date' : undefined) || def.dimension;
      w.breakdown = dim(a.breakdown, 'nodate') || (a.type === 'stacked' ? def.breakdown : '');
      if (w.breakdown === w.dimension) w.breakdown = a.type === 'stacked' ? def.breakdown : '';
      if (a.limit > 0) w.limit = clamp(a.limit, 1, 50);
      break;
    }
    case 'pie':
      w.dimension = dim(a.dimension) || def.dimension;
      w.limit = clamp(a.limit || 6, 2, 7);
      metrics = metrics.filter((r) => !ctx.metric(r).derived);
      if (!metrics.length) metrics = w.metrics;
      break;
    case 'table':
      w.dimension = dim(a.dimension) || def.dimension;
      w.dimension2 = dim(a.dimension2) !== w.dimension ? dim(a.dimension2) : '';
      if (a.limit > 0) w.limit = clamp(a.limit, 1, 500);
      break;
    case 'pivot':
      w.pivotRow = dim(a.pivotRow) || def.pivotRow;
      w.pivotCol = dim(a.pivotCol) || def.pivotCol;
      if (w.pivotRow === w.pivotCol) w.pivotCol = w.pivotRow === 'date_month' ? 'date_week' : 'date_month';
      w.heatmap = !!a.heatmap;
      break;
    default:
  }
  // Tür kuralları: tek metrikli türler, kırılımda tek metrik, grafikte tek birim.
  if (['kpi', 'pie', 'pivot', 'stacked'].includes(a.type) || w.breakdown) metrics = metrics.slice(0, 1);
  else if (CHART_TYPES.includes(a.type)) {
    const fmt = ctx.metric(metrics[0]).fmt;
    metrics = metrics.filter((r) => ctx.metric(r).fmt === fmt).slice(0, 4);
  } else if (a.type === 'table') metrics = metrics.slice(0, 12);
  w.metrics = metrics;
  const used = new Set(metrics.filter((r) => r.startsWith('calc:')).map((r) => r.slice(5)));
  w.calcs = calcs.filter((c) => used.has(c.id));
  return { ...w, size };
}

// Widget → modele bağlam olarak giden sade biçim (parça indeksli).
export function widgetToAi(w) {
  if (w.type === 'text') return { type: 'text', text: w.text || '', width: w.layout?.w, height: w.layout?.h };
  const parts = w.parts || [];
  const idx = Object.fromEntries(parts.map((p, i) => [p.id, i]));
  const calcIdx = Object.fromEntries((w.calcs || []).map((c, i) => [c.id, i]));
  const ref = (r) => { const x = parseRef(r); return x.calc ? { part: -1, metric: '', calc: calcIdx[x.calc] ?? -1 } : { part: idx[x.partId] ?? -1, metric: x.metric, calc: -1 }; };
  return {
    type: w.type, title: w.title || '',
    parts: parts.map((p) => ({ source: p.source, alias: p.alias || '', filters: p.filters || [] })),
    metrics: (w.metrics || []).map(ref),
    calcs: (w.calcs || []).map((c) => ({ label: c.label || '', a: ref(c.a), op: c.op, b: ref(c.b), fmt: c.fmt })),
    dimension: w.dimension || '', dimension2: w.dimension2 || '', breakdown: w.breakdown || '',
    pivotRow: w.pivotRow || '', pivotCol: w.pivotCol || '', limit: w.limit || 0, compare: w.compare || 'none',
    heatmap: !!w.heatmap, datePreset: w.dateMode === 'custom' ? (w.dateRange?.preset || 'report') : 'report',
    dateStart: w.dateMode === 'custom' && w.dateRange?.preset === 'custom' ? w.dateRange.start || '' : '',
    dateEnd: w.dateMode === 'custom' && w.dateRange?.preset === 'custom' ? w.dateRange.end || '' : '',
    width: w.layout?.w, height: w.layout?.h,
  };
}

// Yeni widget'ları mevcutların altına soldan sağa satır satır dizer.
export function placeWidgets(existing, added, cols = 12) {
  let y = existing.reduce((m, w) => Math.max(m, (w.layout?.y || 0) + (w.layout?.h || 0)), 0);
  let x = 0;
  let rowH = 0;
  return added.map(({ size, ...w }) => {
    if (x + size.w > cols) { y += rowH; x = 0; rowH = 0; }
    const layout = { x, y, w: size.w, h: size.h };
    x += size.w;
    rowH = Math.max(rowH, size.h);
    return { ...w, id: newId(), layout };
  });
}
